import type { ChartAppearance } from '@/features/charts/theme/chartAppearance';
import { clock } from '@/services/clock';

import type Highcharts from 'highcharts/highstock';

export type RangeSelectorSeries = {
  data?: readonly unknown[];
};

const createRangeSelector = (
  appearance: ChartAppearance,
  chartSeries: RangeSelectorSeries[]
): Highcharts.RangeSelectorOptions => ({
  labelStyle: {
    display: 'none'
  },
  x: -30,
  buttonSpacing: 8,
  buttonTheme: {
    height: 22,
    width: 40,
    r: 0,
    fill: appearance.navigatorBackground,
    style: {
      color: appearance.foreground
    },
    states: {
      disabled: {
        style: {
          color: appearance.disabledForeground
        },
        fill: appearance.border
      },
      hover: {
        fill: appearance.hoverBackground,
        style: {
          color: appearance.mutedForeground
        }
      },
      select: {
        fill: appearance.accent,
        style: {
          color: appearance.accentForeground
        }
      }
    }
  },
  buttons: [
    {
      type: 'month',
      count: 3,
      text: '3m'
    },
    {
      type: 'year',
      count: 1,
      text: '1y'
    },
    {
      type: 'year',
      count: 5,
      text: '5y'
    },
    {
      type: 'all',
      text: 'Max'
    }
  ],
  selected: determineTimerangeButtonIndex(chartSeries),
  inputEnabled: false
});

const isOneYearBefore = (timestamp: number): boolean => {
  const currentDate = clock.date();
  const oneYearAgo = clock.date();

  oneYearAgo.setFullYear(currentDate.getFullYear() - 1);
  const timestampDate = new Date(timestamp);

  return timestampDate < oneYearAgo;
};

const getPointTimestamp = (point: unknown): number | undefined => {
  if (Array.isArray(point)) {
    return typeof point[0] === 'number' ? point[0] : undefined;
  }
  if (typeof point !== 'object' || point === null || !('x' in point)) return undefined;

  return typeof point.x === 'number' ? point.x : undefined;
};

const determineTimerangeButtonIndex = (chartSeries: RangeSelectorSeries[]): number | undefined => {
  const anySeriesLongerThanYear = chartSeries.some(series => {
    const timestamp = getPointTimestamp(series.data?.[0]);
    return typeof timestamp === 'number' && isOneYearBefore(timestamp);
  });

  return anySeriesLongerThanYear ? 1 : undefined;
};

export default createRangeSelector;
